import React from 'react';
import { Box, Typography, Button } from '@mui/material';

/**
 * AreYouSure - Confirmation dialog content for destructive actions
 * 
 * @param {string} message - Optional warning message to display
 * @param {string[]} bulletPoints - Optional list of items to display as bullet points
 * @param {function} onDelete - Callback when delete button is clicked
 * @param {function} onCancel - Callback when cancel button is clicked
 * @param {string} deleteButtonText - Optional custom text for delete button (default: "Delete")
 * @param {string} cancelButtonText - Optional custom text for cancel button (default: "Cancel")
 */
const AreYouSure = ({
  message,
  bulletPoints = [],
  onDelete,
  onCancel,
  deleteButtonText = 'Delete',
  cancelButtonText = 'Cancel',
}) => {
  const hasWarningContent = message || bulletPoints.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 3,
        }}
      >
        Are you sure?
      </Typography>

      {/* Warning box with message and bullet points */}
      {hasWarningContent && (
        <Box
          sx={{
            backgroundColor: '#FDECEC',
            border: '2px solid #B71C1C',
            borderRadius: 2,
            p: 2,
            mb: 4,
          }}
        >
          {message && (
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: '0.95rem',
                mb: bulletPoints.length > 0 ? 1 : 0,
              }}
            >
              {message}
            </Typography>
          )}

          {bulletPoints.length > 0 && (
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 3,
                '& li': {
                  color: 'text.primary',
                  fontSize: '0.95rem',
                  py: 0.25,
                },
              }}
            >
              {bulletPoints.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          onClick={onDelete}
          sx={{
            backgroundColor: '#8B1C1C',
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#6B1515',
            },
          }}
        >
          {deleteButtonText}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            borderColor: '#333333',
            color: '#333333',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              borderColor: '#333333',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {cancelButtonText}
        </Button>
      </Box>
    </Box>
  );
};

export default AreYouSure;
